import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil } from "lucide-react";
import { AdminLista, AdminTabela } from "@/components/admin/admin-lista";
import { PageHeader, ALVO_TOQUE, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageToolbar, PageSearch } from "@/components/layout/page-toolbar";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell,
} from "@/components/data/data-table";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { combina } from "@/lib/busca";
import { useAtividades, useCreateAtividade, useUpdateAtividade } from "@/hooks/use-equipe";
import { useCulturas } from "@/hooks/use-culturas";
import { useToast } from "@/hooks/use-toast";
import type { Atividade, AtividadeCategoria } from "@shared/schema";
import { ATIVIDADE_CATEGORIA } from "@shared/schema";

// Rótulos da categoria de MANEJO (migração 052). Ela decide o ícone do card de
// operação, se o formulário pergunta volume de calda (`pulverizacao`/`foliar`) e —
// a única regra dura hoje — se a operação pode existir SEM setor (`manutencao`).
const CATEGORIA_LABEL: Record<AtividadeCategoria, string> = {
  fertirrigacao: "Fertirrigação",
  pulverizacao: "Pulverização",
  foliar: "Foliar",
  tratos_culturais: "Tratos culturais",
  manutencao: "Manutenção (pode não ter setor)",
  outro: "Outro",
};

const emptyForm: { nome: string; descricao: string; icone: string; cor: string; culturaId: string | null; categoria: AtividadeCategoria | null } = {
  nome: "", descricao: "", icone: "shovel", cor: "#2D5016", culturaId: null, categoria: null,
};

const COLUNAS_ATIVIDADES = [null, "8rem", "11rem", "4rem"];

// ⚠️ Constante de MÓDULO (dep de useMemo dentro do hook).
const PADRAO_BUSCA = { q: "" };

export default function EquipeAtividades() {
  const { data: atividades = [], isLoading } = useAtividades();
  const { data: culturas = [] } = useCulturas();
  const create = useCreateAtividade();
  const update = useUpdateAtividade();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  // Busca NO CLIENTE, sobre a lista já carregada.
  const { estado, definir } = useEstadoNaUrl(PADRAO_BUSCA);
  const busca = estado.q;
  const [editing, setEditing] = useState<Atividade | null>(null);
  const [form, setForm] = useState(emptyForm);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (a: Atividade) => {
    setEditing(a);
    setForm({
      nome: a.nome, descricao: a.descricao ?? "", icone: a.icone ?? "shovel", cor: a.cor ?? "#2D5016",
      culturaId: a.culturaId ?? null,
      categoria: a.categoria ?? null,
    });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        nome: form.nome.trim(), descricao: form.descricao.trim() || undefined, icone: form.icone, cor: form.cor,
        culturaId: form.culturaId,
        categoria: form.categoria,
      };
      if (editing) await update.mutateAsync({ id: editing.id, data: payload });
      else await create.mutateAsync(payload);
      toast({ title: editing ? "Atividade atualizada!" : "Atividade cadastrada!" });
      setOpen(false);
    } catch {
      toast({ title: "Erro ao salvar atividade", variant: "destructive" });
    }
  };

  const handleToggle = async (a: Atividade, field: "ativo" | "acompanharPorSetor", value: boolean) => {
    try {
      await update.mutateAsync({ id: a.id, data: { [field]: value } });
    } catch {
      toast({ title: "Erro ao atualizar", variant: "destructive" });
    }
  };

  // Só os campos de texto — `culturaId` e `categoria` têm seus próprios Selects
  // com onValueChange (não passam por aqui).
  const field = (key: "nome" | "descricao" | "icone" | "cor") => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [key]: e.target.value })),
  });

  const visiveis = atividades.filter(a => combina(busca, a.nome, a.descricao));

  // `md:p-6`, não `p-6`: no celular o padding extra da página somava aos 12px do
  // AppLayout e era parte do que espremia a linha até o nome da atividade sumir.
  return (
    <div className="md:p-6 space-y-4 md:space-y-6">
      <PageHeader
        titulo="Atividades"
        acao={<Button size="sm" className={ALVO_TOQUE_BOTAO} onClick={openCreate}><Plus className="h-4 w-4 mr-2" />Nova</Button>}
      />

      <PageToolbar
        fim={<PageSearch valor={busca} onChange={v => definir({ q: v })} placeholder="Nome" testId="input-busca-atividades" />}
      >
        <span />
      </PageToolbar>

      {isLoading ? (
        <PageSkeleton variante="tabela" linhas={6} />
      ) : atividades.length === 0 ? (
        <EmptyState
          titulo="Nenhuma atividade cadastrada"
          descricao="A atividade é o que a tarefa do dia registra — sem ela o Plano da semana não tem o que planejar."
          acao={<Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" />Nova atividade</Button>}
          testId="empty-atividades"
        />
      ) : visiveis.length === 0 ? (
        <NadaEncontrado termo={busca} />
      ) : (
        <>
          {/* ── Mobile: card por atividade ──────────────────────────────────
              A tabela antiga era um grid `1fr 120px 160px 40px`: 320px de
              colunas fixas contra os ~284px que sobram num iPhone de 390px, o
              que zerava o `1fr` e apagava o nome da atividade. Aqui os dois
              interruptores viram uma linha de metadado com rótulo próprio —
              cabem sem competir com a identidade. */}
          <AdminLista>
            {visiveis.map(a => {
              const ativo = a.ativo !== false;
              const monitorar = a.acompanharPorSetor !== false;
              return (
                <div key={a.id} className="rounded-xl border bg-card px-3 py-2.5 space-y-2.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className={`h-3 w-3 rounded-full shrink-0 ${!ativo ? "opacity-50" : ""}`} style={{ backgroundColor: a.cor ?? "#2D5016" }} />
                    <div className={`min-w-0 flex-1 ${!ativo ? "opacity-50" : ""}`}>
                      <p className="font-medium text-sm truncate">{a.nome}</p>
                      {a.descricao && <p className="text-xs text-muted-foreground truncate">{a.descricao}</p>}
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className={`${ALVO_TOQUE} shrink-0 text-muted-foreground`}
                      onClick={() => openEdit(a)}
                      aria-label={`Editar ${a.nome}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex items-center justify-between gap-3 border-t pt-2.5">
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                      <Switch
                        checked={ativo}
                        onCheckedChange={v => handleToggle(a, "ativo", v)}
                        aria-label="Ativo"
                      />
                      Ativo
                    </label>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                      <Switch
                        checked={monitorar}
                        onCheckedChange={v => handleToggle(a, "acompanharPorSetor", v)}
                        aria-label="Monitorar setor"
                      />
                      Monitorar setor
                    </label>
                  </div>
                </div>
              );
            })}
          </AdminLista>

          {/* ── Desktop: agora no espec da plataforma ──
              O grid `1fr 120px 160px 40px` que morava aqui somava 320px de
              colunas fixas contra os ~284px de um iPhone — foi o que apagou o
              nome da atividade. No desktop ele nunca foi o problema, mas
              `<colgroup>` diz a mesma coisa sem o risco de colapsar o `1fr`. */}
          <AdminTabela>
            <DataTable colunas={COLUNAS_ATIVIDADES}>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableHead>Atividade</DataTableHead>
                  <DataTableHead className="text-center">Ativo</DataTableHead>
                  <DataTableHead className="text-center">Monitorar setor</DataTableHead>
                  <DataTableHead />
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                {visiveis.map(a => {
                  const ativo = a.ativo !== false;
                  const monitorar = a.acompanharPorSetor !== false;
                  return (
                    <DataTableRow key={a.id}>
                      {/* O nome é a coluna que responde à pergunta da tela. A cor
                          é DADO (`atividades.cor`): entra por `style`. */}
                      <DataTableCell destaque>
                        <div className={`flex items-center gap-2.5 min-w-0 transition-opacity ${!ativo ? "opacity-40" : ""}`}>
                          <div className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: a.cor ?? "#2D5016" }} />
                          <span className="truncate">{a.nome}</span>
                          {a.descricao && <span className="text-xs font-normal text-muted-foreground truncate">{a.descricao}</span>}
                        </div>
                      </DataTableCell>

                      <DataTableCell>
                        <div className="flex items-center justify-center gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Switch
                                checked={ativo}
                                onCheckedChange={v => handleToggle(a, "ativo", v)}
                                aria-label="Ativo"
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              {ativo ? "Desativar (some dos dropdowns)" : "Ativar"}
                            </TooltipContent>
                          </Tooltip>
                          <span className={`text-xs w-10 ${ativo ? "text-sucesso font-medium" : "text-muted-foreground"}`}>
                            {ativo ? "Sim" : "Não"}
                          </span>
                        </div>
                      </DataTableCell>

                      <DataTableCell>
                        <div className="flex items-center justify-center gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Switch
                                checked={monitorar}
                                onCheckedChange={v => handleToggle(a, "acompanharPorSetor", v)}
                                aria-label="Monitorar setor"
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              Exibir na tabela de acompanhamento por setor
                            </TooltipContent>
                          </Tooltip>
                          <span className={`text-xs w-10 ${monitorar ? "text-sucesso font-medium" : "text-muted-foreground"}`}>
                            {monitorar ? "Sim" : "Não"}
                          </span>
                        </div>
                      </DataTableCell>

                      <DataTableCell>
                        <div className="flex justify-center">
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={() => openEdit(a)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </DataTableCell>
                    </DataTableRow>
                  );
                })}
              </DataTableBody>
            </DataTable>
          </AdminTabela>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar Atividade" : "Nova Atividade"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Nome *</Label>
              <Input {...field("nome")} placeholder="Ex: Colheita, Adubação..." />
            </div>
            <div className="space-y-1">
              <Label>Descrição</Label>
              <Input {...field("descricao")} placeholder="Opcional" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Ícone (lucide)</Label>
                <Input {...field("icone")} placeholder="shovel" />
              </div>
              <div className="space-y-1">
                <Label>Cor</Label>
                <div className="flex gap-2">
                  <Input type="color" value={form.cor} onChange={e => setForm(f => ({ ...f, cor: e.target.value }))} className="w-12 p-1 h-9" />
                  <Input {...field("cor")} className="font-mono" />
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Aplica-se à cultura</Label>
              <Select
                value={form.culturaId ?? "__todas"}
                onValueChange={v => setForm(f => ({ ...f, culturaId: v === "__todas" ? null : v }))}
              >
                <SelectTrigger data-testid="select-cultura-alvo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__todas">Todas as culturas</SelectItem>
                  {culturas.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Deixe em "Todas" se a atividade vale para qualquer lavoura. Usado no radar de última atividade por setor.
              </p>
            </div>
            <div className="space-y-1">
              <Label>Categoria de manejo</Label>
              <Select
                value={form.categoria ?? "__sem"}
                onValueChange={v => setForm(f => ({ ...f, categoria: v === "__sem" ? null : (v as AtividadeCategoria) }))}
              >
                <SelectTrigger data-testid="select-categoria-manejo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__sem">Não classificada</SelectItem>
                  {ATIVIDADE_CATEGORIA.map(c => (
                    <SelectItem key={c} value={c}>{CATEGORIA_LABEL[c]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Usada pelas operações do Manejo. Só as de <strong>manutenção</strong> podem ser criadas sem setor.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={!form.nome.trim() || create.isPending || update.isPending}>
              {create.isPending || update.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
